import React, {useState, useEffect} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import './StockPalettes.css';

const API_URL = import.meta.env.VITE_API_URL;

const StockPalettes = ({onNavigate, onLogout, currentPage}) => {
  const {hasPermission} = usePermissions();
  const [palettes, setPalettes] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPalette, setSelectedPalette] = useState(null);
  const [paletteDetails, setPaletteDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editData, setEditData] = useState({x: 0, y: 0, z: 0});

  // États pour l'import Excel
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    statusPalette: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchPalettes();
    fetchStatistics();
  }, [filters.search, filters.statusPalette, pagination.page, pagination.limit]);

  const fetchPalettes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        statusPalette: filters.statusPalette
      });

      const response = await axios.get(`${API_URL}/palettes?${params}`, {
        headers: {Authorization: `Bearer ${token}`}
      });

      if (response.data.success) {
        setPalettes(response.data.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          totalPages: response.data.pagination?.totalPages || 0
        }));
      }
    } catch (error) {
      console.error('Erreur fetchPalettes:', error);
      Swal.fire('Erreur', 'Impossible de charger les palettes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API_URL}/palettes/statistics`, {
        headers: {Authorization: `Bearer ${token}`}
      });

      if (response.data.success) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('Erreur fetchStatistics:', error);
    }
  };

  const fetchPaletteDetails = async (paletteId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/palettes/${paletteId}/details`, {
        headers: {Authorization: `Bearer ${token}`}
      });

      if (response.data.success) {
        setPaletteDetails(response.data.data);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('Erreur fetchPaletteDetails:', error);
      Swal.fire('Erreur', 'Impossible de charger les détails', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPosition = (palette) => {
    setSelectedPalette(palette);
    setEditData({
      x: palette.positionPalette?.x || 0,
      y: palette.positionPalette?.y || 0,
      z: palette.positionPalette?.z || 0
    });
    setShowEditModal(true);
  };

  const handleSavePosition = async () => {
    try {
      const response = await axios.patch(
        `${API_URL}/palettes/${selectedPalette.palette}/position`,
        editData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        Swal.fire('Succès', 'Position mise à jour', 'success');
        setShowEditModal(false);
        fetchPalettes();
      }
    } catch (error) {
      console.error('Erreur handleSavePosition:', error);
      Swal.fire('Erreur', 'Impossible de mettre à jour la position', 'error');
    }
  };

  const handleUpdateNumeroSerieStatus = async (articleId, newStatus) => {
    try {
      const response = await axios.patch(
        `${API_URL}/palettes/numero-serie/${articleId}/status`,
        {statusNumeroSerie: newStatus},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Statut mis à jour',
          timer: 1500,
          showConfirmButton: false
        });

        // Rafraîchir les détails
        if (paletteDetails) {
          fetchPaletteDetails(paletteDetails.paletteInfo.palette);
        }
        fetchPalettes();
      }
    } catch (error) {
      console.error('Erreur handleUpdateNumeroSerieStatus:', error);
      Swal.fire('Erreur', 'Impossible de mettre à jour le statut', 'error');
    }
  };

  const handleReactiverPalette = async (paletteId) => {
    const result = await Swal.fire({
      title: 'Réactiver la palette ?',
      text: 'Tous les numéros de série seront remis à "disponible"',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, réactiver',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.patch(
          `${API_URL}/palettes/${paletteId}/reactiver`,
          {},
          {
            headers: {Authorization: `Bearer ${token}`}
          }
        );

        if (response.data.success) {
          Swal.fire('Succès', 'Palette réactivée', 'success');
          fetchPalettes();
          if (showDetailsModal) {
            setShowDetailsModal(false);
          }
        }
      } catch (error) {
        console.error('Erreur handleReactiverPalette:', error);
        Swal.fire('Erreur', 'Impossible de réactiver la palette', 'error');
      }
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      previewExcel(selectedFile);
    }
  };

  const previewExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, {type: 'array'});
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        setPreviewData(jsonData.slice(0, 5));
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de lire le fichier Excel'
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (!file) {
      Swal.fire({
        icon: 'warning',
        title: 'Attention',
        text: 'Veuillez sélectionner un fichier'
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/stock/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const details = response.data.details || {};
        let detailsHtml = `<div style="text-align: left; margin-top: 15px;">
          <p><strong>Total de lignes :</strong> ${details.totalLines || 0}</p>
          <p><strong>Importées :</strong> <span style="color: green;">${details.imported || 0}</span></p>
          ${details.errors > 0 ? `<p><strong>Erreurs :</strong> <span style="color: red;">${details.errors}</span></p>` : ''}
          ${details.duplicates > 0 ? `<p><strong>Doublons ignorés :</strong> <span style="color: orange;">${details.duplicates}</span></p>` : ''}
        </div>`;

        Swal.fire({
          icon: 'success',
          title: 'Succès',
          html: `${response.data.message}${detailsHtml}`,
          confirmButtonColor: '#1e3a5f'
        });

        setFile(null);
        setFileName('');
        setPreviewData([]);
        setShowImportModal(false);
        const fileInput = document.getElementById('fileInputPalettes');
        if (fileInput) fileInput.value = '';

        fetchPalettes();
        fetchStatistics();
      }
    } catch (error) {
      console.error('Erreur handleImport:', error);
      const errorData = error.response?.data;
      let errorMessage = errorData?.message || error.message || 'Une erreur est survenue lors de l\'import';

      if (errorData?.details) {
        const details = errorData.details;
        let detailsHtml = `<div style="text-align: left; margin-top: 15px;">`;

        if (details.totalLines) {
          detailsHtml += `<p><strong>Total de lignes :</strong> ${details.totalLines}</p>`;
        }
        if (details.imported !== undefined) {
          detailsHtml += `<p><strong>Importées :</strong> <span style="color: green;">${details.imported}</span></p>`;
        }
        if (details.errors > 0) {
          detailsHtml += `<p><strong>Erreurs :</strong> <span style="color: red;">${details.errors}</span></p>`;

          if (details.errorDetails && details.errorDetails.length > 0) {
            detailsHtml += `<div style="margin-top: 10px; max-height: 200px; overflow-y: auto; border: 1px solid #ddd; padding: 10px; border-radius: 5px;">`;
            details.errorDetails.slice(0, 10).forEach(err => {
              detailsHtml += `<p style="margin: 5px 0; font-size: 12px;"><strong>Ligne ${err.line}:</strong> ${err.errors.join(', ')}</p>`;
            });
            if (details.errorDetails.length > 10) {
              detailsHtml += `<p style="margin: 5px 0; font-size: 12px; color: #999;">... et ${details.errorDetails.length - 10} autres erreurs</p>`;
            }
            detailsHtml += `</div>`;
          }
        }
        if (details.duplicates > 0) {
          detailsHtml += `<p><strong>Doublons ignorés :</strong> <span style="color: orange;">${details.duplicates}</span></p>`;
        }

        detailsHtml += `</div>`;
        errorMessage = detailsHtml;
      }

      Swal.fire({
        icon: 'error',
        title: 'Erreur d\'import',
        html: errorMessage,
        confirmButtonColor: '#dc2626',
        width: '600px'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    const result = await Swal.fire({
      title: '⚠️ ATTENTION !',
      html: '<p style="font-size: 16px; margin: 20px 0;">Vous êtes sur le point de supprimer <strong>TOUS</strong> les articles du stock.</p><p style="color: #dc2626; font-weight: bold;">Cette action est IRRÉVERSIBLE !</p>',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Oui, tout supprimer',
      cancelButtonText: 'Annuler',
      input: 'text',
      inputPlaceholder: 'Tapez "SUPPRIMER" pour confirmer',
      inputValidator: (value) => {
        if (value !== 'SUPPRIMER') {
          return 'Vous devez taper "SUPPRIMER" pour confirmer';
        }
      }
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}/stock/items/all`, {
          headers: {Authorization: `Bearer ${token}`}
        });

        Swal.fire({
          icon: 'success',
          title: 'Suppression réussie',
          text: `${response.data.deletedCount} articles supprimés`,
          timer: 3000,
          showConfirmButton: false
        });

        fetchPalettes();
        fetchStatistics();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Erreur lors de la suppression'
        });
      }
    }
  };

  const getStatusPaletteBadge = (status) => {
    const badges = {
      active: {label: 'Active', class: 'badge-success'},
      reserve: {label: 'Réservée', class: 'badge-warning'},
      archive: {label: 'Archivée', class: 'badge-secondary'}
    };
    return badges[status] || badges.active;
  };

  const getStatusNumeroSerieBadge = (status) => {
    return status === 'disponible'
      ? {label: 'Disponible', class: 'badge-success'}
      : {label: 'Réservé', class: 'badge-danger'};
  };

  return (
    <div className="stock-palettes-container">
      <BackOfficeMenu
        onNavigate={onNavigate}
        onLogout={onLogout}
        currentPage={currentPage}
      />
      <div className="stock-palettes-content">
        {/* En-tête avec titre et actions */}
        <div className="dashboard-header">
          <div>
            <h1>Gestion des Palettes</h1>
            <p>Administration centralisée du stock par palettes</p>
          </div>
          <div className="header-actions">
  {hasPermission('stock', 'delete') && (
    <button
      className="btn-delete-all"
      disabled
      title="Fonction temporairement désactivée"
    >
      <span>🗑️</span>
      Supprimer tout
    </button>
  )}

  {hasPermission('stock', 'create') && (
    <button
      className="btn-import"
      disabled
      title="Fonction temporairement désactivée"
    >
      <span>📥</span>
      Import Excel
    </button>
  )}
</div>
        </div>

        {/* Cartes statistiques */}
        <div className="stats-cards-grid">
          <div className="stat-card blue">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <h3>{statistics?.palettes?.active || 0}</h3>
              <p>Palettes Actives</p>
            </div>
          </div>
          <div className="stat-card orange">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <h3>{statistics?.palettes?.reserve || 0}</h3>
              <p>Palettes Réservées</p>
            </div>
          </div>
          <div className="stat-card gray">
            <div className="stat-icon">📂</div>
            <div className="stat-content">
              <h3>{statistics?.palettes?.archive || 0}</h3>
              <p>Palettes Archivées</p>
            </div>
          </div>
          <div className="stat-card green">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3>{statistics?.articles?.active || 0}</h3>
              <p>Articles Disponibles</p>
            </div>
          </div>
        </div>

        {/* Filtres */}
        <div className="filters-container">
          <input
            type="text"
            placeholder="🔍 Rechercher par palette, référence..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            className="search-input"
          />
          <select
            value={filters.statusPalette}
            onChange={(e) => setFilters({...filters, statusPalette: e.target.value})}
            className="status-select"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actives</option>
            <option value="reserve">Réservées</option>
            <option value="archive">Archivées</option>
          </select>
        </div>

        {/* Tableau des palettes */}
        {loading ? (
          <LoadingSpinner message="Chargement des palettes..." />
        ) : (
          <div className="palettes-table-container">
            <table className="palettes-table">
              <thead>
                <tr>
                  <th>PALETTE</th>
                  <th>RÉFÉRENCE</th>
                  <th>STATUT</th>
                  <th>POSITION (X,Y,Z)</th>
                  <th>QTÉ TOTALE</th>
                  <th>DISPONIBLE</th>
                  <th>RÉSERVÉE</th>
                  <th>PRODUIT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {palettes.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="no-data">
                      Aucune palette trouvée
                    </td>
                  </tr>
                ) : (
                  palettes.map((palette) => {
                    const statusBadge = getStatusPaletteBadge(palette.statusPalette);
                    return (
                      <tr key={palette._id}>
                        <td><strong>{palette.palette}</strong></td>
                        <td>{palette.reference || 'N/A'}</td>
                        <td>
                          <span className={`status-badge ${statusBadge.class}`}>
                            {statusBadge.label}
                          </span>
                        </td>
                        <td>
                          <small>
                            X:{palette.positionPalette?.x || 0},
                            Y:{palette.positionPalette?.y || 0},
                            Z:{palette.positionPalette?.z || 0}
                          </small>
                        </td>
                        <td><strong>{palette.totalArticles}</strong></td>
                        <td className="text-success">{palette.quantiteDisponible}</td>
                        <td className="text-danger">{palette.quantiteReservee}</td>
                        <td>
                          {palette.produit ? palette.produit.nomProduit : 'N/A'}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-detail"
                              onClick={() => fetchPaletteDetails(palette.palette)}
                              title="Voir détails"
                            >
                              👁️
                            </button>
                            {hasPermission('stock', 'update') && (
                              <button
                                className="btn-edit"
                                onClick={() => handleEditPosition(palette)}
                                title="Modifier position"
                              >
                                ✏️
                              </button>
                            )}
                            {palette.statusPalette === 'archive' && hasPermission('stock', 'update') && (
                              <button
                                className="btn-reactivate"
                                onClick={() => handleReactiverPalette(palette.palette)}
                                title="Réactiver"
                              >
                                🔄
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="pagination-bar">
          <button
            disabled={pagination.page === 1}
            onClick={() => setPagination({...pagination, page: pagination.page - 1})}
          >
            ◀ Précédent
          </button>
          <span>Page {pagination.page} sur {pagination.totalPages || 1}</span>
          <button
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => setPagination({...pagination, page: pagination.page + 1})}
          >
            Suivant ▶
          </button>
        </div>

        {/* Modal Détails Palette */}
        {showDetailsModal && paletteDetails && (
          <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📦 Détails de la palette {paletteDetails.paletteInfo.palette}</h2>
                <button className="modal-close" onClick={() => setShowDetailsModal(false)}>✕</button>
              </div>

              <div className="modal-body">
                <div className="palette-info-grid">
                  <div className="info-item">
                    <strong>Référence:</strong> {paletteDetails.paletteInfo.reference}
                  </div>
                  <div className="info-item">
                    <strong>Statut:</strong>{' '}
                    <span className={`status-badge ${getStatusPaletteBadge(paletteDetails.paletteInfo.statusPalette).class}`}>
                      {getStatusPaletteBadge(paletteDetails.paletteInfo.statusPalette).label}
                    </span>
                  </div>
                  <div className="info-item">
                    <strong>Total articles:</strong> {paletteDetails.paletteInfo.totalArticles}
                  </div>
                  <div className="info-item">
                    <strong>Disponibles:</strong> <span className="text-success">{paletteDetails.paletteInfo.quantiteDisponible}</span>
                  </div>
                  <div className="info-item">
                    <strong>Réservés:</strong> <span className="text-danger">{paletteDetails.paletteInfo.quantiteReservee}</span>
                  </div>
                  <div className="info-item">
                    <strong>Position:</strong> X:{paletteDetails.paletteInfo.positionPalette?.x},
                    Y:{paletteDetails.paletteInfo.positionPalette?.y},
                    Z:{paletteDetails.paletteInfo.positionPalette?.z}
                  </div>
                </div>

                <h3 className="section-title">Liste des numéros de série</h3>
                <div className="articles-table-container">
                  <table className="articles-table">
                    <thead>
                      <tr>
                        <th>N° SÉRIE</th>
                        <th>ARTICLE</th>
                        <th>RÉF. PRODUIT</th>
                        <th>PRODUIT LIÉ</th>
                        <th>STATUT</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paletteDetails.articles.map((article) => {
                        const statusBadge = getStatusNumeroSerieBadge(article.statusNumeroSerie);
                        return (
                          <tr key={article._id}>
                            <td><code>{article.numeroSerie}</code></td>
                            <td>{article.article}</td>
                            <td>{article.referenceProduit}</td>
                            <td>{article.produit ? article.produit.nomProduit : 'N/A'}</td>
                            <td>
                              <span className={`status-badge ${statusBadge.class}`}>
                                {statusBadge.label}
                              </span>
                            </td>
                            <td>
                              {hasPermission('stock', 'update') && (
                                article.statusNumeroSerie === 'disponible' ? (
                                  <button
                                    className="btn-reserve"
                                    onClick={() => handleUpdateNumeroSerieStatus(article._id, 'reserve')}
                                  >
                                    📌 Réserver
                                  </button>
                                ) : (
                                  <button
                                    className="btn-liberate"
                                    onClick={() => handleUpdateNumeroSerieStatus(article._id, 'disponible')}
                                  >
                                    🔓 Libérer
                                  </button>
                                )
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Modifier Position */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>✏️ Modifier la position</h2>
                <button className="modal-close" onClick={() => setShowEditModal(false)}>✕</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label>Position X:</label>
                  <input
                    type="number"
                    value={editData.x}
                    onChange={(e) => setEditData({...editData, x: parseInt(e.target.value) || 0})}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Position Y:</label>
                  <input
                    type="number"
                    value={editData.y}
                    onChange={(e) => setEditData({...editData, y: parseInt(e.target.value) || 0})}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Position Z:</label>
                  <input
                    type="number"
                    value={editData.z}
                    onChange={(e) => setEditData({...editData, z: parseInt(e.target.value) || 0})}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setShowEditModal(false)}>
                  Annuler
                </button>
                <button className="btn-save" onClick={handleSavePosition}>
                  💾 Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal d'import Excel */}
        {showImportModal && (
          <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
            <div className="modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>📥 Importer un fichier Excel</h2>
                <button className="modal-close" onClick={() => setShowImportModal(false)}>✕</button>
              </div>

              <div className="modal-body">
                <div className="file-upload-section">
                  <label className="file-upload-label">
                    <input
                      type="file"
                      id="fileInputPalettes"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      style={{display: 'none'}}
                    />
                    <div className="file-upload-box">
                      <span className="upload-icon">📁</span>
                      <p>{fileName || 'Cliquez pour sélectionner un fichier Excel'}</p>
                      <small>Formats acceptés: .xlsx, .xls (Colonnes: Referenz, Palette, Seriennummer, Artikel, Beschreibung)</small>
                    </div>
                  </label>
                </div>

                {previewData.length > 0 && (
                  <div className="preview-section">
                    <h3>Aperçu des données (5 premières lignes)</h3>
                    <div className="preview-table-container">
                      <table className="preview-table">
                        <thead>
                          <tr>
                            <th>Referenz</th>
                            <th>Palette</th>
                            <th>Seriennummer</th>
                            <th>Artikel</th>
                            <th>Beschreibung</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.map((row, index) => (
                            <tr key={index}>
                              <td>{row.Referenz || row.referenz || '-'}</td>
                              <td>{row.Palette || row.palette || '-'}</td>
                              <td>{row.Seriennummer || row.seriennummer || '-'}</td>
                              <td>{row.Artikel || row.artikel || '-'}</td>
                              <td>{row.Beschreibung || row.beschreibung || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn-cancel"
                  onClick={() => setShowImportModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn-import-modal"
                  onClick={handleImport}
                  disabled={!file || isLoading}
                >
                  {isLoading ? 'Import en cours...' : 'Importer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockPalettes;
